import { Command } from 'commander';
import { WorkflowManager } from '../core/workflow-manager';
import { logger } from '../utils/logger';
import pc from 'picocolors';

export function createConfigCommand(): Command {
  const command = new Command('config');
  command.description('Manage workflow configuration');

  // Show config
  command
    .command('show')
    .description('Show current configuration')
    .option('-j, --json', 'Output as JSON')
    .action(async (options) => {
      try {
        const manager = new WorkflowManager();
        const config = await manager.loadConfig();

        if (options.json) {
          console.log(JSON.stringify(config, null, 2));
          return;
        }

        logger.info(pc.bold('\n⚙️  Workflow Configuration\n'));
        
        logger.info(pc.bold('Project:'));
        logger.info(`  Name: ${pc.cyan(config.project.name)}`);
        logger.info(`  Type: ${pc.cyan(config.project.type)}`);
        logger.info(`  Language: ${pc.cyan(config.project.language)}`);
        logger.info(`  Frameworks: ${pc.cyan(config.project.framework.join(', ') || 'none')}`);
        
        logger.info('');
        logger.info(pc.bold('Settings:'));
        logger.info(`  Auto sync: ${config.settings.autoSync ? pc.green('enabled') : pc.red('disabled')}`);
        logger.info(`  Sync interval: ${pc.cyan(config.settings.syncInterval / 1000 + 's')}`);
        logger.info(`  AI features: ${config.settings.enableAI ? pc.green('enabled') : pc.red('disabled')}`);
        logger.info(`  Check mode: ${pc.cyan(config.settings.checkMode)}`);
        
        if (config.paths) {
          logger.info('');
          logger.info(pc.bold('Paths:'));
          if (config.paths.todoFile) {
            logger.info(`  TODO file: ${pc.cyan(config.paths.todoFile)}`);
          }
          if (config.paths.workflowDir) {
            logger.info(`  Workflow dir: ${pc.cyan(config.paths.workflowDir)}`);
          }
        }
        
        logger.info('');
      } catch (error: any) {
        logger.error('Failed to load configuration:', error.message);
        process.exit(1);
      }
    });

  // Set config value
  command
    .command('set <key> <value>')
    .description('Set configuration value')
    .action(async (key, value) => {
      try {
        const manager = new WorkflowManager();
        const config = await manager.loadConfig();

        // Parse the key path (e.g., "settings.autoSync")
        const keys = key.split('.');
        let current: any = config;
        
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }

        const lastKey = keys[keys.length - 1];
        const oldValue = current[lastKey];

        // Convert value types
        if (value === 'true') value = true;
        else if (value === 'false') value = false;
        else if (!isNaN(Number(value))) value = Number(value);

        current[lastKey] = value;

        // Save config
        await (manager as any).saveConfig(config);

        logger.success(`✅ Configuration updated`);
        logger.info(`   ${key}: ${pc.gray(String(oldValue))} → ${pc.green(String(value))}`);
      } catch (error: any) {
        logger.error('Failed to update configuration:', error.message);
        process.exit(1);
      }
    });

  // Get config value
  command
    .command('get <key>')
    .description('Get configuration value')
    .action(async (key) => {
      try {
        const manager = new WorkflowManager();
        const config = await manager.loadConfig();

        // Parse the key path
        const keys = key.split('.');
        let value: any = config;
        
        for (const k of keys) {
          if (value && typeof value === 'object' && k in value) {
            value = value[k];
          } else {
            logger.error(`Configuration key not found: ${key}`);
            process.exit(1);
          }
        }

        logger.info(String(value));
      } catch (error: any) {
        logger.error('Failed to get configuration:', error.message);
        process.exit(1);
      }
    });

  return command;
}